<template>
  <div class="podcast-generated-videos-page" :class="{ embedded }">
    <template v-if="!embedded">
      <h1 class="page-title">Social Library</h1>
      <div class="tabs">
        <div class="tab active">Video Snippets</div>
      </div>
    </template>

    <div class="filters">
      <div class="filter-field dropdown-field">
        <label>Podcaster</label>
        <Dropdown
          :modelValue="selectedPodcasterId"
          :options="podcasters"
          optionLabel="name"
          optionValue="podcaster_id"
          placeholder="All podcasters"
          showClear
          class="w-full"
          @update:modelValue="selectPodcaster"
        >
          <template #value="slotProps">
            <span v-if="slotProps.value">{{
              truncateLabel(
                podcasters.find((p) => p.podcaster_id === slotProps.value)?.name
              )
            }}</span>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
        </Dropdown>
      </div>
      <div class="filter-field dropdown-field">
        <label>Show</label>
        <Dropdown
          :modelValue="selectedShowId"
          :options="shows"
          optionLabel="show_title"
          optionValue="show_id"
          placeholder="All shows"
          showClear
          class="w-full"
          @update:modelValue="selectShow"
        >
          <template #value="slotProps">
            <span v-if="slotProps.value">{{
              truncateLabel(
                shows.find((s) => s.show_id === slotProps.value)?.show_title
              )
            }}</span>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
        </Dropdown>
      </div>
      <div v-if="!isGroupAdminViewer" class="filter-field dropdown-field">
        <label>Seller Group</label>
        <Dropdown
          :modelValue="selectedSellerGroupId"
          :options="sellerGroups"
          optionLabel="name"
          optionValue="id"
          placeholder="All seller groups"
          showClear
          class="w-full"
          @update:modelValue="selectSellerGroup"
        >
          <template #value="slotProps">
            <span v-if="slotProps.value">{{
              truncateLabel(
                sellerGroups.find((g) => g.id === slotProps.value)?.name
              )
            }}</span>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
        </Dropdown>
      </div>
      <div class="filter-field period-field">
        <label>Period</label>
        <div class="clearable-field">
          <Calendar
            :modelValue="dateRange"
            selectionMode="range"
            :numberOfMonths="1"
            showIcon
            placeholder="Select date range"
            class="w-full"
            @update:modelValue="selectDateRange"
          />
          <i
            v-if="dateRange"
            class="pi pi-times clear-icon"
            title="Clear"
            @click="selectDateRange(null)"
          />
        </div>
      </div>
      <div class="filter-field length-field">
        <label>Length (sec)</label>
        <div class="clearable-field">
          <div class="len-inputs">
            <InputNumber
              :modelValue="minLength"
              placeholder="Min"
              :useGrouping="false"
              @update:modelValue="(value) => selectLength(value, maxLength)"
            />
            <span class="len-sep">–</span>
            <InputNumber
              :modelValue="maxLength"
              placeholder="Max"
              :useGrouping="false"
              @update:modelValue="(value) => selectLength(minLength, value)"
            />
          </div>
          <i
            v-if="minLength != null || maxLength != null"
            class="pi pi-times clear-icon"
            title="Clear"
            @click="selectLength(null, null)"
          />
        </div>
      </div>
    </div>

    <div class="lib-header">
      <span class="lib-count"
        ><b>{{ totalVideos }}</b> clips</span
      >
      <Button
        icon="pi pi-refresh"
        text
        rounded
        aria-label="Refresh"
        v-tooltip.bottom="'Refresh'"
        :loading="loading"
        @click="refresh"
      />
    </div>

    <div v-if="loading" class="state-message">Loading…</div>
    <div v-else-if="!videos.length" class="state-message">No videos found.</div>
    <div v-else class="grid">
      <VideoCard
        v-for="video in videos"
        :key="video.asset_id"
        :video="video"
        :id="video.asset_id"
        :url="video.asset_url"
        :duration="video.metadata.duration"
        :created-at="video.created_at"
        :download-filename="`${video.episode_title || video.asset_id}.mp4`"
        :meta-lines="[
          { label: 'Show:', text: video.show_title },
          { label: 'Episode:', text: video.episode_title || '—', muted: true },
        ]"
        :active-playing-id="currentlyPlayingId"
        :deleting-id="deletingId"
        :lazy-load="false"
        :extra-actions="[
          { key: 'show-info', icon: 'pi pi-info-circle', title: 'Details' },
          {
            key: 'fetch-transcript',
            title: 'Transcript',
            label: 'T',
            loading: transcriptLoadingId === video.asset_id,
          },
        ]"
        @play="setPlaying"
        @copy-link="copyLink"
        @delete="confirmDeleteVideo"
        @action="
          (key, v) =>
            key === 'show-info' ? openInfoDialog(v) : fetchTranscript(v)
        "
      />
    </div>

    <div class="paginator-row">
      <Paginator
        :rows="pageSize"
        :totalRecords="totalVideos"
        :first="(currentPage - 1) * pageSize"
        template="PrevPageLink PageLinks NextPageLink"
        @page="onPage"
      />
    </div>

    <VideoInfoDialog
      v-model:visible="infoDialogVisible"
      :video="infoDialogVideo"
      @copy-link="copyLink"
    />

    <TranscriptDialog
      v-model:visible="transcriptDialogVisible"
      :text="transcriptText"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import Calendar from "primevue/calendar";
import InputNumber from "primevue/inputnumber";
import Paginator from "primevue/paginator";
import { usePodcastGeneratedVideos } from "./composables/usePodcastGeneratedVideos";
import VideoCard from "@/components/shared/VideoCard.vue";
import VideoInfoDialog from "./shared/VideoInfoDialog.vue";
import TranscriptDialog from "../Snippets/TranscriptDialog.vue";
import {
  snippetApiToken,
  snippetViewerRole,
} from "@/composables/useSnippetApiToken";

// eslint-disable-next-line no-undef
const props = defineProps({
  embedded: { type: Boolean, default: false },
  mode: { type: String, default: "static" },
  initialShowId: { type: String, default: null },
});

const token = snippetApiToken();
const isGroupAdminViewer = snippetViewerRole() === "group-admin";
const route = useRoute();

const {
  videos,
  totalVideos,
  currentPage,
  loading,
  pageSize,
  podcasters,
  shows,
  sellerGroups,
  selectedPodcasterId,
  selectedShowId,
  selectedSellerGroupId,
  dateRange,
  minLength,
  maxLength,
  currentlyPlayingId,
  deletingId,
  transcriptDialogVisible,
  transcriptLoadingId,
  transcriptText,
  fetchFilterOptions,
  fetchVideos,
  fetchTranscript,
  confirmDeleteVideo,
  selectPodcaster,
  selectShow,
  selectSellerGroup,
  selectDateRange,
  selectLength,
  onPage,
  setPlaying,
  copyLink,
} = usePodcastGeneratedVideos(token, props.mode);

const truncateLabel = (text, max = 14) =>
  text && text.length > max ? `${text.slice(0, max)}...` : text;

const refresh = () => {
  fetchFilterOptions();
  fetchVideos();
};

const infoDialogVisible = ref(false);
const infoDialogVideo = ref(null);

const openInfoDialog = (video) => {
  infoDialogVideo.value = video;
  infoDialogVisible.value = true;
};

onMounted(() => {
  const showId = props.embedded ? props.initialShowId : route.query.showId;
  if (showId) selectedShowId.value = showId;
  fetchFilterOptions();
  fetchVideos();
});

watch(
  () => props.initialShowId,
  (showId) => {
    if (!props.embedded || !showId) return;
    selectedShowId.value = showId;
    currentPage.value = 1;
    fetchVideos();
  }
);
</script>

<style scoped>
.podcast-generated-videos-page {
  padding: 24px 32px 60px 32px;
}

.podcast-generated-videos-page.embedded {
  padding: 0;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  margin: 2px 0 16px 0;
  letter-spacing: -0.2px;
}

.tabs {
  display: flex;
  gap: 28px;
  border-bottom: 1px solid var(--surface-border);
  margin-bottom: 22px;
}

.tab {
  font-size: 14px;
  color: var(--text-color-secondary);
  padding-bottom: 12px;
  font-weight: 500;
}

.tab.active {
  color: var(--primary-color);
  font-weight: 700;
  border-bottom: 2px solid var(--primary-color);
}

.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 22px;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-field label {
  font-size: 11.5px;
  font-weight: 600;
  color: var(--text-color-secondary);
}

.dropdown-field {
  width: 170px;
  min-width: 0;
}

.period-field {
  width: 225px;
  min-width: 0;
}

.length-field {
  min-width: 150px;
}

.len-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
}

.len-inputs :deep(.p-inputnumber-input) {
  width: 120px;
}

.len-sep {
  color: var(--surface-400);
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
}

.clearable-field {
  display: flex;
  align-items: center;
  gap: 6px;
}

.clear-icon {
  color: var(--text-color-secondary);
  cursor: pointer;
  font-size: 13px;
}

.clear-icon:hover {
  color: var(--primary-color);
}

.lib-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.lib-count {
  font-size: 13px;
  color: var(--text-color-secondary);
}

.state-message {
  padding: 40px 0;
  text-align: center;
  color: var(--text-color-secondary);
}

.grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
}

@media (max-width: 1300px) {
  .grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 1000px) {
  .grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 600px) {
  .grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.paginator-row {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}

.paginator-row :deep(.p-paginator) {
  background: transparent;
  border: none;
}
</style>
